import { useEffect, useRef, useState } from 'react';

const CLOSE_BLUR_DELAY = 50;

interface UseExpandableSearchParams {
  onOpen?: () => void;
  onClose?: () => void;
  onEnter?: (value: string) => void;
  value?: string;
}

const useExpandableSearch = ({ onOpen, onClose, onEnter, value }: UseExpandableSearchParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTriggerHidden, setIsTriggerHidden] = useState(false);
  const isOpenRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearBlurTimeout = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const open = () => {
    clearBlurTimeout();
    if (isOpenRef.current) {
      return;
    }
    isOpenRef.current = true;
    setIsOpen(true);
    onOpen?.();
  };

  const collapse = () => {
    clearBlurTimeout();
    if (!isOpenRef.current) {
      return;
    }
    isOpenRef.current = false;
    setIsOpen(false);
    onClose?.();
  };

  const focus = () => {
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    clearBlurTimeout();
    blurTimeoutRef.current = setTimeout(collapse, CLOSE_BLUR_DELAY);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      collapse();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onEnter?.(value ?? '');
    }
  };

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setIsTriggerHidden(true);
      return;
    }
    const timeout = setTimeout(() => setIsTriggerHidden(false), CLOSE_BLUR_DELAY);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => clearBlurTimeout, []);

  return {
    isOpen,
    isTriggerHidden,
    open,
    collapse,
    focus,
    inputRef,
    handleBlur,
    onInputKeyDown,
  };
};

export default useExpandableSearch;
